// Формы пиктограмм и разбор нотации макета. SMY-66
//
// Отдельно от Icon.tsx по двум причинам. Первая практическая: node снимает
// типы, но JSX не трансформирует, — импортируй тест `.tsx`, и он не
// запустится. Вторая по существу: таблица форм это данные, взятые из
// design/README.md, и проверять их надо как данные.
//
// Формы записаны ровно в той нотации, что в таблице «Пиктограммы задач».
// Переведи я `circle(10.5,10.5,r6.5)` в путь руками — появился бы второй
// источник истины, который сверяют глазами. Здесь сверять нечего, а
// расхождение с макетом ловит icons.test.ts, читающий тот же README.
//
// Общие правила оттуда же: viewBox 24×24, `stroke-width: 1.75` (в мелких
// контекстах 1.9–2), `stroke-linecap: round`, `fill: none`, цвет `accent`.

export const ICON_SHAPES = {
  search: ['circle(10.5,10.5,r6.5)', 'M15.4 15.4 20 20'],
  check: ['circle(12,12,r8.5)', 'M8 12.3l3 3 5-6'],
  research: ['circle(12,12,r2.5)', 'circle(12,12,r6)', 'circle(12,12,r9.5)'],
  write: ['M4.5 19.5h5l9.5-9.5-5-5L4.5 14.5z', 'M13 6.5l4.5 4.5'],
  docReview: ['rect(5,3,14,18,rx2.5)', 'M9 12.5l2.5 2.5L16 9.5'],
  docCreate: ['rect(5,3,14,18,rx2.5)', 'M12 9v6M9 12h6'],
  image: ['rect(3.5,5,17,14,rx2.5)', 'circle(9,10.5,r1.6)', 'M6 17l4.5-4 3.5 3 2.5-2.5 3 3'],
  menu: ['M4 7h16M4 12h16M4 17h10'],
  back: ['M15 5l-7 7 7 7'],
  chevron: ['M9 5l7 7-7 7'],
  attach: ['circle(12,12,r9)', 'M12 8v8M8 12h8'],
  mic: ['rect(9,3,6,11,rx3)', 'M5.5 11.5a6.5 6.5 0 0013 0M12 18v3'],
  time: ['circle(12,12,r9)', 'M12 7.5v5l3.5 2'],
  tick: ['M4 12.5l5 5L20 6.5'],
  send: ['M12 19V6M6 12l6-6 6 6'],
  more: ['circle(12,5,r1.4)', 'circle(12,12,r1.4)', 'circle(12,19,r1.4)'],

  // Иконки хрома. В таблице README их нет — она называется «Пиктограммы
  // ЗАДАЧ» и хром в неё не входил. Формы взяты из веб-макета, где они
  // используются: профиль в левой колонке, плюс на кнопках создания,
  // крестик на закрытии окна.
  profile: ['circle(12,8.5,r3.5)', 'M5.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5'],
  plus: ['M12 6v12M6 12h12'],
  close: ['M6 6l12 12M18 6L6 18'],

  /**
   * Значок «i». ЕДИНСТВЕННАЯ форма, которой нет ни в таблице README, ни в
   * веб-макете, — и она отмечена как исключение в icons.test.ts.
   *
   * Взяться ей оттуда неоткуда: информера в макетах не было вовсе, он появился
   * 06.09.2026, когда пометку закона убрали из тела карточки под значок.
   * Собрана из словаря самого набора: окружность как у `time` и `attach`,
   * прямые отрезки той же обводки. Придёт макет с настоящим значком —
   * заменить, и тест перестанет считать её исключением.
   */
  info: ['circle(12,12,r9)', 'M12 11v5.5', 'M12 7.6v0.1'],

  /**
   * Треугольник с «!» — плашка-предупреждение о коде из СМС и данных карты
   * (SMY-551, решение владельца 26.10.2026: «выделенный цветом и иконкой»).
   * Исключение из дизайна по той же причине, что «i»: предупреждений в
   * макетах не было. Собран из словаря набора: скругления как у `docReview`,
   * знак — как у `info`.
   */
  warning: ['M10.3 4.6a2 2 0 013.4 0l7.5 12.9a2 2 0 01-1.7 3H4.5a2 2 0 01-1.7-3z', 'M12 9.5v4.5', 'M12 17v0.1'],

  /**
   * Канцелярская кнопка — закреплённый чат. Второе исключение из дизайна по
   * той же причине, что и «i»: закреплять чаты в макетах было нечего.
   *
   * До неё на этом месте стояла галочка из таблицы задач, и читалась она как
   * «выполнено», а не «закреплено» — то есть говорила не то.
   */
  pin: ['M9 4h6', 'M10 4v6l-3 3.5h10L14 10V4', 'M12 13.5V20'],

  /**
   * Два листа — «скопировать». Третье исключение из дизайна: кнопки копии в
   * макетах не было, копирование жило текстовой кнопкой «Поделиться».
   *
   * Собрана из словаря набора: те же скруглённые прямоугольники, что у
   * `image` и `docReview`, тот же радиус 2,5.
   */
  copy: ['rect(9,9,11,11,rx2.5)', 'M15 6.5A2.5 2.5 0 0012.5 4h-6A2.5 2.5 0 004 6.5v6A2.5 2.5 0 006.5 15'],

  /**
   * Палец вверх и палец вниз — оценка результата (SMY-123). Четвёртое и пятое
   * исключение из дизайна: экрана с оценкой в макетах не рисовали вовсе.
   *
   * Заводятся ПАРОЙ и зеркальны друг другу по горизонтали: у дуг отличается
   * только флаг направления. Нарисуй мы их порознь, «хорошо» и «плохо»
   * получились бы разного веса — и кнопка, которую жмут чаще, оказалась бы
   * той, которая просто заметнее.
   *
   * Собраны из словаря набора: скруглённый прямоугольник ладони с тем же
   * радиусом, что у `pin`, и дуги той же обводки.
   */
  thumbUp: [
    'rect(3,10.5,4.5,9.5,rx1.5)',
    'M7.5 11.5l4-7.6a1.8 1.8 0 013.3 1.2L14 10.5h4.2a2 2 0 011.9 2.6l-1.6 5.6a2.5 2.5 0 01-2.4 1.8H7.5',
  ],
  thumbDown: [
    'rect(3,4,4.5,9.5,rx1.5)',
    'M7.5 12.5l4 7.6a1.8 1.8 0 003.3-1.2L14 13.5h4.2a2 2 0 001.9-2.6l-1.6-5.6A2.5 2.5 0 0016 3.5H7.5',
  ],
} as const satisfies Record<string, readonly string[]>

export type IconName = keyof typeof ICON_SHAPES

/**
 * Названия из таблицы README. По ним тест сверяется с документом; у иконок
 * хрома строки в таблице нет, и они сверяются с веб-макетом.
 */
export const ICON_TITLES: Record<IconName, string> = {
  search: 'Найти',
  check: 'Проверить',
  research: 'Исследовать',
  write: 'Написать',
  docReview: 'Разобрать документ',
  docCreate: 'Создать документ',
  image: 'Изображение',
  menu: 'Меню',
  back: 'Назад',
  chevron: 'Шеврон вперёд',
  attach: 'Прикрепить',
  mic: 'Микрофон',
  time: 'Время',
  tick: 'Галочка',
  send: 'Отправить',
  more: 'Ещё',
  profile: 'Профиль',
  plus: 'Плюс',
  close: 'Закрыть',
  info: 'Что нужно знать',
  warning: 'Предупреждение',
  pin: 'Закреплено',
  copy: 'Скопировать',
  thumbUp: 'Подошло',
  thumbDown: 'Не подошло',
}

export type Shape =
  | { kind: 'circle'; cx: number; cy: number; r: number }
  | { kind: 'rect'; x: number; y: number; width: number; height: number; rx: number }
  | { kind: 'path'; d: string }

const CIRCLE = /^circle\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*r([\d.]+)\s*\)$/
const RECT = /^rect\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*rx([\d.]+)\s*\)$/

/**
 * Разбирает одну форму из нотации макета.
 *
 * Всё, что не круг и не прямоугольник, считается путём — но не молча:
 * непонятая форма отрисовалась бы пустотой, а заметить пустую иконку можно
 * только глазами. Поэтому строка, похожая на вызов функции, но не подошедшая
 * ни под один разбор, — это ошибка, а не путь.
 */
export function parseShape(shape: string): Shape {
  const circle = CIRCLE.exec(shape)
  if (circle) {
    const [, cx, cy, r] = circle
    return { kind: 'circle', cx: Number(cx), cy: Number(cy), r: Number(r) }
  }

  const rect = RECT.exec(shape)
  if (rect) {
    const [, x, y, width, height, rx] = rect
    return { kind: 'rect', x: Number(x), y: Number(y), width: Number(width), height: Number(height), rx: Number(rx) }
  }

  if (!/^[Mm]/.test(shape)) throw new Error(`форма «${shape}» не разобрана и не похожа на путь`)
  return { kind: 'path', d: shape }
}
